import React, { FC } from "react";
import classNames from "classnames";
import loadable from "@loadable/component";
import { Fade } from "react-awesome-reveal";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { FileType, checkFileType } from "@app/helpers/check-file-type";
import { IRichText, TextType } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { ContentAlign, ViewVariant } from "../../enums";
import { Asset } from "../../models";

const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title: IRichText<TextType>;
  media: Asset;
  overlayColorOpacity?: number;
  contentAlign?: ContentAlign;
  viewVariant?: ViewVariant;
};

export const ImageTitle: FC<Props> = ({
  title,
  media,
  overlayColorOpacity,
  contentAlign,
  viewVariant,
}) => (
  <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
    <div
      className={classNames(
        "relative mt-20 h-[514px]",
        "lg:mt-25",
        "xl:mt-40",
        "xxl:h-[843px]",
        { "mx-5 lg:mx-16 xl:mx-24": viewVariant === ViewVariant.CONTAINER },
      )}
    >
      <div
        className={classNames("absolute top-0 right-0 bottom-0 left-0", {
          "xxl:mx-auto xxl:max-w-container":
            viewVariant === ViewVariant.CONTAINER,
        })}
      >
        {checkFileType(media.filename) === FileType.VIDEO ? (
          <ReactPlayer
            className={classNames("h-full [&>video]:object-cover", {
              "[&>video]:border-divider [&>video]:xxl:rounded-4xl [&>video]:rounded-lg [&>video]:border":
                viewVariant === ViewVariant.CONTAINER,
              "[&>video]:rounded-none": viewVariant !== ViewVariant.CONTAINER,
            })}
            url={media.filename}
            width="100%"
            height="none"
            playing
            muted
            loop
            playsinline
            config={{ file: { attributes: { disablePictureInPicture: true } } }}
          />
        ) : checkFileType(media.filename) === FileType.IMAGE ? (
          <GatsbyImage
            image={getGatsbyImage(media.filename, { layout: "fullWidth" })}
            alt={media.alt}
            className={classNames("h-full w-full", {
              "border-divider xxl:rounded-4xl rounded-lg border":
                viewVariant === ViewVariant.CONTAINER,
              "rounded-none": viewVariant !== ViewVariant.CONTAINER,
            })}
          />
        ) : null}
        <div
          className={classNames(
            "bg-base-content absolute top-0 right-0 bottom-0 left-0",
            { "rounded-2xl": viewVariant === ViewVariant.CONTAINER },
          )}
          style={{ opacity: `${overlayColorOpacity ?? 0}%` }}
        />
      </div>

      <div
        className={classNames(
          "flex h-full flex-col justify-center",
          "xxl:mx-auto",
          {
            "items-center": contentAlign === ContentAlign.CENTER,
            "items-start": !contentAlign || contentAlign === ContentAlign.LEFT,
            "items-end": contentAlign === ContentAlign.RIGHT,
            "xxl:max-w-container-wide px-5 lg:px-16 xl:px-24":
              viewVariant !== ViewVariant.CONTAINER,
            "xxl:max-w-container": viewVariant === ViewVariant.CONTAINER,
          },
        )}
      >
        <BlockHeader
          richText={title}
          className={classNames(
            "text-large text-primary-content relative break-words",
            "md:max-w-[70%]",
            "lg:text-xx-large",
            "xxl:max-w-[50%] xxl:text-huge",
            {
              "text-center": contentAlign === ContentAlign.CENTER,
              "text-start": !contentAlign || contentAlign === ContentAlign.LEFT,
              "text-end": contentAlign === ContentAlign.RIGHT,
              "xxl:p-11 p-5 lg:p-8": viewVariant === ViewVariant.CONTAINER,
            },
          )}
        />
      </div>
    </div>
  </Fade>
);
